import { FormHelperTextProps, IconButton, InputAdornment, TextFieldProps } from '@mui/material';
import { ChangeEvent } from 'react';
import { useState } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { TextInput } from '../TextField/TextInput';
import SvgEyeClose from '~/components/icons/EyeClose.tsx';
import SvgEyeOpen from '~/components/icons/EyeOpen.tsx';
import { getErrorKey } from '~/helpers/utils';
import { styles } from '~/components/FormComponents/styles.ts';

export type TFormPasswordProps = Omit<TextFieldProps, 'ref'> & {
  name: string;
  errorTouchedOnly?: boolean;
  rules?: Omit<RegisterOptions, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
};

export const FormPassword = ({ errorTouchedOnly, rules, ...props }: TFormPasswordProps) => {
  const { control, setValue } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordType = props.type === 'password';
  const inputSlot = typeof props.slotProps?.input === 'object' ? props.slotProps.input : undefined;

  return (
    <Controller
      control={control}
      name={props.name}
      rules={rules}
      render={({ field: { ref, onChange, ...field }, fieldState, formState }) => {
        const showError =
          (errorTouchedOnly
            ? fieldState.isTouched
            : fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;
        const errorText = showError ? (fieldState.error?.message as string) : '';
        const errorDataTestId = [field.name, getErrorKey(errorText)].join('-');

        const handleChange = (newValue: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          onChange(newValue);
          props.onChange?.(newValue);
        };

        return (
          <TextInput
            {...field}
            value={field?.value || ''}
            inputRef={ref}
            {...props}
            type={isPasswordType && showPassword ? 'text' : props.type}
            onChange={handleChange}
            error={!!showError}
            helperText={errorText}
            onBlur={(e) => {
              field.onBlur();
              setValue(field.name, e.target.value.trim(), {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            slotProps={{
              ...props.slotProps,
              htmlInput: {
                ...props.slotProps?.htmlInput,
                'data-testid': field.name,
              },
              input: {
                ...props.slotProps?.input,
                endAdornment: isPasswordType ? (
                  <InputAdornment position="end" sx={styles.textContainer}>
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      tabIndex={-1}
                      disableRipple
                      sx={styles.iconButton}
                    >
                      {showPassword ? <SvgEyeClose /> : <SvgEyeOpen />}
                    </IconButton>
                  </InputAdornment>
                ) : (
                  inputSlot?.endAdornment
                ),
              },
              formHelperText: {
                ...props.slotProps?.formHelperText,
                'data-testid': errorDataTestId,
              } as FormHelperTextProps & { 'data-testid': string },
            }}
          />
        );
      }}
    />
  );
};
